import { Platform } from 'react-native';

import { getFeatureFlag, isFeatureFlagEnabled } from '@/modules/analytics';
import { isMobileWeb } from '@/modules/clientContext';

import { FEATURE_FLAGS } from '../constants/featureFlags';

/**
 * Desktop → web-app redirect at onboarding completion (Sparta's
 * `checkOnboardingCompleteEpic`). Web only — and onboarding is served under
 * `/onboarding` on the web app's origin, so `/app` and `/` are same-origin.
 *
 * The PostHog cohort for `DESKTOP_USERS_DIRECT_TO_WEB_APP` targets desktop +
 * non-SSO users, but the cohort is not the only desktop gate — see `isMobileWeb`
 * below. On the `'test'` variant we set the app-download-drawer session cookie
 * (read by the web app's home) and leave onboarding for the web app: `/app` when
 * `WEB_APP_REDIRECT` is on, otherwise home. Both flags are evaluated on the fly
 * here (the user is identified by completion), not snapshotted at auth.
 *
 * Returns `true` if a redirect was issued (the caller should stop), `false`
 * otherwise (control variant / native / mobile web / no window → stay on the
 * transition screen).
 */
export async function maybeRedirectToWebApp(): Promise<boolean> {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;

  // Dev builds serve only the onboarding app on the origin — there's no web-app
  // `/app` (or home) to land on — so the completion redirect would break the
  // local flow. Skip it in dev; production web still redirects.
  if (__DEV__) return false;

  // Phone browsers finish onboarding at the transition screen, which hands them
  // to the app with a one-time login token (DownloadPanel → resolveDownloadUrl).
  // Redirecting them into the web app instead strands them there and they never
  // reach that handoff.
  //
  // INTENTIONAL Sparta divergence: Sparta leans entirely on the PostHog cohort to
  // keep this desktop-only. Staging's cohort does not filter, so mobile testers
  // were redirected to /app/home and never saw the transition screen at all.
  // When the flow depends on device targeting, that targeting belongs in code,
  // not only in flag config. Checked BEFORE the flag reads so a mobile session
  // can't set the app-download-drawer cookie on its way out either.
  if (isMobileWeb()) return false;

  const directToWebApp =
    (await getFeatureFlag(FEATURE_FLAGS.DESKTOP_USERS_DIRECT_TO_WEB_APP)) === 'test';
  if (!directToWebApp) return false;

  // Read the target flag BEFORE writing the cookie — so a (theoretical) rejected
  // read can't leave the app-download-drawer cookie behind without a redirect.
  const toWebApp = await isFeatureFlagEnabled(FEATURE_FLAGS.WEB_APP_REDIRECT);
  // Session cookie the web app's home page reads to surface the app-download drawer.
  document.cookie = 'showAppDownloadDrawer=true; path=/; SameSite=Strict; Secure';
  // replace() (not assign()) so the back button can't return to completed onboarding.
  window.location.replace(toWebApp ? '/app' : '/');
  return true;
}
